import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { CheckCircle2, Columns, Combine, Copy, Edit, FileDown, FileText, Loader2, Plus, Search } from 'lucide-react'
import toast from 'react-hot-toast'

import { showError, showSuccess } from '@/lib/swal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { scGoodsReceiptNoteService } from '@/services/scGoodsReceiptNoteService'
import { useAuthStore } from '@/store/auth.store'
import type { SupplyChainGrnRow } from '../types'

const PAGE_SIZE = 10

function apiErrorMessage(err: unknown, fallback: string): string {
  if (!isAxiosError(err)) return err instanceof Error ? err.message : fallback
  const d = err.response?.data
  if (d && typeof d === 'object') {
    const o = d as Record<string, unknown>
    const m = o.message ?? o.Message ?? o.title ?? o.Title
    if (typeof m === 'string' && m.trim()) return m.trim()
  }
  if (typeof err.message === 'string' && err.message.trim()) return err.message.trim()
  return fallback
}

type DataColumnKey = 'code' | 'reference' | 'vendor' | 'orderCode' | 'date' | 'total' | 'convertToBill'

const DATA_COLUMNS: { key: DataColumnKey; label: string }[] = [
  { key: 'code', label: 'Code' },
  { key: 'reference', label: 'Reference' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'orderCode', label: 'Order Code' },
  { key: 'date', label: 'Date' },
  { key: 'total', label: 'Total' },
  { key: 'convertToBill', label: 'Convert to bill' },
]

export const SupplyChainGoodsReceiptNotesList: React.FC = () => {
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [rows, setRows] = useState<SupplyChainGrnRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [convertingId, setConvertingId] = useState<string | null>(null)
  const [combinedBillSubmitting, setCombinedBillSubmitting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [visibleColumns, setVisibleColumns] = useState<Record<DataColumnKey, boolean>>({
    code: true,
    reference: true,
    vendor: true,
    orderCode: true,
    date: true,
    total: true,
    convertToBill: true,
  })

  const fetchList = useCallback(
    async (withSpinner: boolean) => {
      if (!branchId) return
      try {
        if (withSpinner) setLoading(true)
        const res = await scGoodsReceiptNoteService.getAll(branchId, pageNumber, PAGE_SIZE)
        setRows(res.items)
        setTotalCount(res.totalCount)
      } catch {
        if (withSpinner) {
          setRows([])
          setTotalCount(0)
          toast.error('Failed to load goods receipt notes.')
        } else {
          toast.error('Action completed, but refreshing the list failed.')
        }
      } finally {
        if (withSpinner) setLoading(false)
      }
    },
    [branchId, pageNumber],
  )

  useEffect(() => {
    void fetchList(true)
  }, [fetchList])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.reference, r.vendor, r.orderCode, r.date, r.total].some((x) =>
        String(x ?? '').toLowerCase().includes(q),
      ),
    )
  }, [rows, searchQuery])

  const eligibleForCombined = useMemo(() => filtered.filter((r) => !r.isBillCreated), [filtered])
  const eligibleIdSet = useMemo(() => new Set(eligibleForCombined.map((r) => r.id)), [eligibleForCombined])
  const allEligibleSelected =
    eligibleForCombined.length > 0 && eligibleForCombined.every((r) => selectedIds.includes(r.id))

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => eligibleIdSet.has(id)))
  }, [eligibleIdSet])

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const toggleSelectAllEligible = () => {
    if (allEligibleSelected) {
      const drop = new Set(eligibleForCombined.map((r) => r.id))
      setSelectedIds((prev) => prev.filter((id) => !drop.has(id)))
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const r of eligibleForCombined) next.add(r.id)
        return [...next]
      })
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const startIdx = totalCount === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(pageNumber * PAGE_SIZE, totalCount)

  const fmtTotal = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : String(n))
  const fmtDate = (value?: string) => {
    if (!value) return '-'
    const d = new Date(`${value}T12:00:00`)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString('en-GB')
  }

  const visibleDataCount = Object.values(visibleColumns).filter(Boolean).length
  const tableColSpan = visibleDataCount + 2

  const toggleColumn = (key: DataColumnKey) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))

  const handleCopy = () => {
    if (filtered.length === 0) return toast.error('Nothing to copy.')
    const header = ['Code', 'Reference', 'Vendor', 'Order Code', 'Date', 'Total']
    const lines = filtered.map((r) =>
      [r.code, r.reference, r.vendor, r.orderCode, r.date, fmtTotal(r.total)].join('\t'),
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success('Copied.')
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) return toast.error('Nothing to export.')
    const header = 'Code,Reference,Vendor,OrderCode,Date,Total'
    const body = filtered
      .map((r) =>
        [r.code, r.reference, r.vendor, r.orderCode, r.date, fmtTotal(r.total)]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `supply_chain_goods_receipt_notes_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('CSV downloaded.')
  }

  const handleConvertToBill = async (r: SupplyChainGrnRow) => {
    if (r.isBillCreated) {
      await showError('Already converted', 'This goods receipt note is already converted to a bill.')
      return
    }
    if (!branchId) return toast.error('Select a branch first.')
    const grnId = Number(r.id)
    if (!Number.isFinite(grnId) || grnId <= 0) return toast.error('Invalid goods receipt note id.')
    try {
      setConvertingId(r.id)
      await scGoodsReceiptNoteService.convertToBillIndividually(branchId, grnId, '')
      await fetchList(false)
      await showSuccess('Converted to bill', 'The goods receipt note has been converted to a bill successfully.')
    } catch (err) {
      await showError('Failed to convert', apiErrorMessage(err, 'Failed to convert to bill.'))
    } finally {
      setConvertingId(null)
    }
  }

  const handleCombinedBill = async () => {
    const ids = selectedIds.filter((id) => eligibleIdSet.has(id))
    if (ids.length === 0) return toast.error('Select at least one goods receipt note that is not yet converted to a bill.')
    if (!branchId) return toast.error('Select a branch first.')
    const grnIds = ids.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0)
    if (grnIds.length === 0) return toast.error('Invalid selection.')
    try {
      setCombinedBillSubmitting(true)
      await scGoodsReceiptNoteService.convertToBillCombined(branchId, grnIds, '')
      setSelectedIds([])
      await fetchList(false)
      await showSuccess('Combined bill created', 'The selected goods receipt notes have been combined into a bill successfully.')
    } catch (err) {
      await showError('Failed to create combined bill', apiErrorMessage(err, 'Failed to create combined bill.'))
    } finally {
      setCombinedBillSubmitting(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Goods Receipt Notes</h1>
            <p className="text-muted-foreground text-sm">Supply Chain — Goods Receipt Note Details</p>
          </div>
        </div>

        <Button onClick={() => navigate('/supply-chain/goods-receipt-notes/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Goods Receipt Note
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search (current page)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" type="button">
                    <Columns className="mr-2 h-4 w-4" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {DATA_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleCombinedBill()}
                disabled={
                  combinedBillSubmitting ||
                  loading ||
                  convertingId !== null ||
                  selectedIds.length === 0
                }
              >
                {combinedBillSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Combine className="mr-2 h-4 w-4" />
                )}
                Combined bill{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-primary/20 bg-primary/5">
                  <th className="h-12 w-[52px] px-2 text-center font-bold text-primary">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={allEligibleSelected}
                        disabled={
                          eligibleForCombined.length === 0 ||
                          combinedBillSubmitting ||
                          loading ||
                          convertingId !== null
                        }
                        onCheckedChange={() => toggleSelectAllEligible()}
                        aria-label="Select all eligible goods receipt notes on this page"
                      />
                    </div>
                  </th>
                  {visibleColumns.code && <th className="h-12 px-4 text-center font-bold text-primary">Code</th>}
                  {visibleColumns.reference && (
                    <th className="h-12 px-4 text-center font-bold text-primary">Reference</th>
                  )}
                  {visibleColumns.vendor && <th className="h-12 px-4 text-center font-bold text-primary">Vendor</th>}
                  {visibleColumns.orderCode && (
                    <th className="h-12 px-4 text-center font-bold text-primary">Order Code</th>
                  )}
                  {visibleColumns.date && <th className="h-12 px-4 text-center font-bold text-primary">Date</th>}
                  {visibleColumns.total && <th className="h-12 px-4 text-center font-bold text-primary">Total</th>}
                  {visibleColumns.convertToBill && (
                    <th className="h-12 min-w-[9rem] px-2 text-center font-bold text-primary">Convert to bill</th>
                  )}
                  <th className="h-12 w-[100px] px-2 text-center font-bold text-primary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="py-12 text-center text-muted-foreground">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  filtered.map((r, i) => {
                    const busy = convertingId !== null
                    const convertDisabled = busy || r.isBillCreated
                    const canSelectForCombined = !r.isBillCreated
                    return (
                      <tr
                        key={r.id || `row-${pageNumber}-${i}`}
                        className="border-b transition-colors hover:bg-primary/5"
                      >
                        <td className="w-[52px] px-2 py-2 text-center">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={selectedIds.includes(r.id)}
                              disabled={!canSelectForCombined || combinedBillSubmitting || loading || busy}
                              onCheckedChange={() => toggleSelectRow(r.id)}
                              aria-label={`Select goods receipt note ${r.code || r.id}`}
                            />
                          </div>
                        </td>
                        {visibleColumns.code && <td className="px-4 py-2 text-center text-sm">{r.code || '-'}</td>}
                        {visibleColumns.reference && (
                          <td className="px-4 py-2 text-center text-sm">{r.reference || '-'}</td>
                        )}
                        {visibleColumns.vendor && <td className="px-4 py-2 text-center text-sm">{r.vendor || '-'}</td>}
                        {visibleColumns.orderCode && (
                          <td className="px-4 py-2 text-center text-sm">{r.orderCode || '-'}</td>
                        )}
                        {visibleColumns.date && <td className="px-4 py-2 text-center text-sm">{fmtDate(r.date)}</td>}
                        {visibleColumns.total && (
                          <td className="px-4 py-2 text-center text-sm">{fmtTotal(r.total)}</td>
                        )}
                        {visibleColumns.convertToBill && (
                          <td className="px-2 py-2 text-center">
                            {r.isBillCreated ? (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
                                title="Already converted to bill"
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                                Converted
                              </span>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                disabled={convertDisabled}
                                onClick={() => void handleConvertToBill(r)}
                              >
                                {convertingId === r.id ? (
                                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
                                ) : null}
                                Convert to bill
                              </Button>
                            )}
                          </td>
                        )}
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-primary/10"
                              disabled={convertDisabled}
                              title={r.isBillCreated ? 'Already converted to bill' : 'Edit'}
                              aria-label="Edit"
                              onClick={() => navigate(`/supply-chain/goods-receipt-notes/${r.id}/edit`)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              Showing {startIdx} to {endIdx} of {totalCount} entries
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber <= 1 || loading}
              >
                Prev
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">
                {pageNumber} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={pageNumber >= totalPages || loading}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

